import api from "../auth/api";
import { getAccessToken } from "../auth/authService";
import { publicApi } from "../publicApi";
import type {
  CreatePortfolioFolderRequest,
  CreatePortfolioFolderResponse,
  DepositDemoCashRequest,
  DepositDemoCashResponse,
  GalleryTradesQuery,
  GalleryTradesResponse,
  PortfolioFollowStateResponse,
  PortfolioPerformanceRange,
  PortfolioPerformanceResponse,
  SavePortfolioHoldingsRequest,
  SavePortfolioHoldingsResponse,
  GetDemoAccountResponse,
  GetDemoTradesQuery,
  GetDemoTradesResponse,
  GetPortfolioFoldersQuery,
  GetPortfolioFoldersResponse,
  GetPortfolioStocksQuery,
  GetPortfolioStocksResponse,
  GetPortfolioGalleryPreviewQuery,
  GetPortfolioGalleryPreviewResponse,
  GetPortfolioGalleryQuery,
  GetPortfolioGalleryResponse,
  GetPublicPortfolioQuery,
  GetPublicPortfolioResponse,
  PlacePortfolioTradeRequest,
  PlacePortfolioTradeResponse,
  PortfolioAnalyticsResponse,
  PortfolioFolder,
  UpdatePortfolioAccessRequest,
  UpdatePortfolioAccessResponse,
  UpdatePortfolioFolderRequest,
  UpdatePortfolioFolderResponse,
} from "./portfolio.type";

export const getPortfolioFolders = async (
  query: GetPortfolioFoldersQuery = {}
): Promise<GetPortfolioFoldersResponse> => {
  const response = await api.get<GetPortfolioFoldersResponse>(
    "/portfolio/folders/my",
    {
      params: query,
    }
  );

  return response.data;
};

export const createPortfolioFolder = async (
  data: CreatePortfolioFolderRequest
): Promise<CreatePortfolioFolderResponse> => {
  const response = await api.post<CreatePortfolioFolderResponse>(
    "/portfolio/folders",
    data
  );

  return response.data;
};

export const updatePortfolioFolder = async (
  payload: UpdatePortfolioFolderRequest
): Promise<UpdatePortfolioFolderResponse> => {
  const { id, name, banner, description, galleries, show_in_gallery } =
    payload;
  const response = await api.put<UpdatePortfolioFolderResponse>(
    `/portfolio/folders/${id}`,
    {
      name,
      ...(banner !== undefined ? { banner } : {}),
      ...(description !== undefined ? { description } : {}),
      ...(galleries !== undefined ? { galleries } : {}),
      ...(show_in_gallery !== undefined ? { show_in_gallery } : {}),
    }
  );

  return response.data;
};

export const deletePortfolioFolder = async (id: string): Promise<void> => {
  await api.delete(`/portfolio/folders/${id}`);
};

export const getPortfolioStocks = async (
  query: GetPortfolioStocksQuery
): Promise<GetPortfolioStocksResponse> => {
  const response = await api.get<GetPortfolioStocksResponse>(
    "/portfolio/stocks",
    {
      params: query,
    }
  );

  return response.data;
};

export const buyPortfolioStock = async (
  data: PlacePortfolioTradeRequest
): Promise<PlacePortfolioTradeResponse> => {
  const { folder_id, ticker, quantity } = data;
  const response = await api.post<PlacePortfolioTradeResponse>(
    `/portfolio/folders/${folder_id}/buy`,
    { ticker, quantity }
  );

  return response.data;
};

export const sellPortfolioStock = async (
  data: PlacePortfolioTradeRequest
): Promise<PlacePortfolioTradeResponse> => {
  const { folder_id, ticker, quantity } = data;
  const response = await api.post<PlacePortfolioTradeResponse>(
    `/portfolio/folders/${folder_id}/sell`,
    { ticker, quantity }
  );

  return response.data;
};

export const getDemoAccount = async (
  folderId: string
): Promise<GetDemoAccountResponse> => {
  const response = await api.get<GetDemoAccountResponse>(
    `/portfolio/folders/${folderId}/account`
  );

  return response.data;
};

export const resetDemoAccount = async (
  folderId: string
): Promise<GetDemoAccountResponse> => {
  const response = await api.post<GetDemoAccountResponse>(
    `/portfolio/folders/${folderId}/reset`
  );

  return response.data;
};

export const getDemoTrades = async (
  folderId: string,
  query: GetDemoTradesQuery = {}
): Promise<GetDemoTradesResponse> => {
  const response = await api.get<GetDemoTradesResponse>(
    `/portfolio/folders/${folderId}/trades`,
    { params: query }
  );

  return response.data;
};

export const getPortfolioAnalytics = async (
  portfolioId: string
): Promise<PortfolioAnalyticsResponse> => {
  const response = await api.get<PortfolioAnalyticsResponse>(
    `/portfolio/folders/${portfolioId}/analytics`
  );

  return response.data;
};

export const updatePortfolioAccess = async (
  payload: UpdatePortfolioAccessRequest
): Promise<UpdatePortfolioAccessResponse> => {
  const { id, access_level } = payload;
  const response = await api.post<UpdatePortfolioAccessResponse>(
    `/portfolio/folders/${id}/access`,
    { access_level }
  );

  return response.data;
};

export const getPortfolioFolderById = async (
  id: string
): Promise<GetPortfolioGalleryPreviewResponse | PortfolioFolder> => {
  const response = await api.get<
    GetPortfolioGalleryPreviewResponse | PortfolioFolder
  >(`/portfolio/folders/${id}`);

  return response.data;
};

export const getPortfolioGallery = async (
  query: GetPortfolioGalleryQuery = {}
): Promise<GetPortfolioGalleryResponse> => {
  // Guests must not use the auth client: a 401 there logs the session out.
  const client = getAccessToken() ? api : publicApi;
  const response = await client.get<GetPortfolioGalleryResponse>(
    "/portfolio/gallery",
    { params: query }
  );

  return response.data;
};

export const getPortfolioGalleryPreview = async (
  id: string,
  query: GetPortfolioGalleryPreviewQuery = {}
): Promise<GetPortfolioGalleryPreviewResponse> => {
  const response = await api.get<GetPortfolioGalleryPreviewResponse>(
    `/portfolio/gallery/${id}`,
    { params: query }
  );

  return response.data;
};

export const getPublicPortfolio = async (
  hash: string,
  query?: GetPublicPortfolioQuery
): Promise<GetPublicPortfolioResponse> => {
  const response = await api.get<GetPublicPortfolioResponse>(
    `/public/portfolio-folders/${hash}/portfolios`,
    { params: query }
  );

  return response.data;
};

// ---- Ready-made portfolios (staff only) ----

/** POST /portfolio/folders/{id}/holdings, or /holdings/preview when preview is true. */
export const savePortfolioHoldings = async (
  folderId: string,
  body: SavePortfolioHoldingsRequest,
  options?: { preview?: boolean },
): Promise<SavePortfolioHoldingsResponse> => {
  const suffix = options?.preview ? "/preview" : "";
  const response = await api.post<SavePortfolioHoldingsResponse>(
    `/portfolio/folders/${folderId}/holdings${suffix}`,
    body,
  );

  return response.data;
};

export const depositDemoCash = async (
  data: DepositDemoCashRequest,
): Promise<DepositDemoCashResponse> => {
  const { folder_id, amount } = data;
  const response = await api.post<DepositDemoCashResponse>(
    `/portfolio/folders/${folder_id}/deposit`,
    { amount },
  );

  return response.data;
};

// ---- Ready-made portfolios: follow (free) and public trade history ----

export const getPortfolioFollowState = async (
  id: string,
): Promise<PortfolioFollowStateResponse> => {
  const response = await api.get<PortfolioFollowStateResponse>(
    `/portfolio/gallery/${id}/follow`,
  );
  return response.data;
};

export const followPortfolio = async (
  id: string,
): Promise<PortfolioFollowStateResponse> => {
  const response = await api.post<PortfolioFollowStateResponse>(
    `/portfolio/gallery/${id}/follow`,
  );
  return response.data;
};

export const unfollowPortfolio = async (
  id: string,
): Promise<PortfolioFollowStateResponse> => {
  const response = await api.delete<PortfolioFollowStateResponse>(
    `/portfolio/gallery/${id}/follow`,
  );
  return response.data;
};

export const getFollowedPortfolios = async (
  query: GetPortfolioGalleryQuery = {},
): Promise<GetPortfolioGalleryResponse> => {
  const response = await api.get<GetPortfolioGalleryResponse>(
    "/portfolio/gallery/following",
    { params: query },
  );
  return response.data;
};

export const getGalleryTrades = async (
  id: string,
  query: GalleryTradesQuery = {},
): Promise<GalleryTradesResponse> => {
  const response = await api.get<GalleryTradesResponse>(
    `/portfolio/gallery/${id}/trades`,
    { params: query },
  );
  return response.data;
};

export const getPortfolioPerformance = async (
  id: string,
  range: PortfolioPerformanceRange,
): Promise<PortfolioPerformanceResponse> => {
  const response = await api.get<PortfolioPerformanceResponse>(
    `/portfolio/gallery/${id}/performance`,
    { params: { range } },
  );
  return response.data;
};
